import React, { FC, useState } from 'react';
import { BiError } from 'react-icons/bi';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { IoWarningOutline } from 'react-icons/io5';

import CircularProgress from '@mui/material/CircularProgress';

import BasicButton from '../../../../components/buttons/basicButton';
import AampeIconBackground from '../../../../components/shared/AampeIconBackground/AampeIconBackground';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';

import { TestMessageDeliveryPhase } from '../../../../hooks/formulas/useTestMessageDeliveryStatus';

import {
    TestMessageResultItem,
    TestMessageResultStatus,
} from '../../../../models/formula.model';

interface TestMessageDeliveryStatusProps {
    phase: TestMessageDeliveryPhase;
    results: TestMessageResultItem[];
    checkAgain: () => void;
}

export const formatFailureDetail = (result?: TestMessageResultItem): string => {
    const fallback = 'The provider did not accept this message.';
    if (!result) return fallback;

    const parts: string[] = [];
    if (result.error_detail) parts.push(result.error_detail);
    if (result.provider_status_code) {
        parts.push(`(HTTP ${result.provider_status_code})`);
    }
    return parts.length ? parts.join(' ') : fallback;
};

const TestMessageDeliveryStatus: FC<TestMessageDeliveryStatusProps> = ({
    phase,
    results,
    checkAgain,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const total = results.length;
    const failedCount = results.filter(
        (result) => result.status === TestMessageResultStatus.Failed,
    ).length;

    return (
        <Container>
            {phase === 'checking' && (
                <Row>
                    <CircularProgress size={16} />
                    <Text>Checking delivery status…</Text>
                </Row>
            )}
            {phase === 'delivered' && (
                <Row>
                    <AampeIconBackground
                        primaryColor={aampeTheme.colors.success50}
                        secondaryColor={aampeTheme.colors.success100}
                    >
                        <IoMdCheckmarkCircleOutline
                            color={aampeTheme.colors.success500}
                        />
                    </AampeIconBackground>
                    <Text>
                        {total > 1
                            ? `Delivered to all ${total} contacts.`
                            : 'Delivered.'}
                    </Text>
                </Row>
            )}
            {phase === 'failed' && (
                <Row>
                    <AampeIconBackground
                        primaryColor={aampeTheme.colors.error50}
                        secondaryColor={aampeTheme.colors.error100}
                    >
                        <BiError color={aampeTheme.colors.error500} />
                    </AampeIconBackground>
                    <Text>
                        {total > 1
                            ? `${failedCount} of ${total} contacts failed to receive the message.`
                            : formatFailureDetail(results[0])}
                    </Text>
                </Row>
            )}
            {phase === 'timed_out' && (
                <Row>
                    <AampeIconBackground
                        primaryColor={aampeTheme.colors.warning50}
                        secondaryColor={aampeTheme.colors.warning50}
                    >
                        <IoWarningOutline
                            color={aampeTheme.colors.warning500}
                        />
                    </AampeIconBackground>
                    <Text>We haven&apos;t confirmed delivery yet.</Text>
                    <BasicButton
                        name="Check again"
                        isPrimary={false}
                        isCompact
                        onClick={checkAgain}
                    />
                </Row>
            )}
            {total > 1 && (phase === 'delivered' || phase === 'failed') && (
                <DetailsToggle onClick={() => setShowDetails((prev) => !prev)}>
                    {showDetails ? 'Hide' : 'View'} details for {total} contacts
                </DetailsToggle>
            )}
            {showDetails && total > 1 && (
                <DetailsList>
                    {results.map((result) => (
                        <DetailsRow key={result.contact_id}>
                            <span>{result.contact_id}</span>
                            <span>
                                {result.status ===
                                TestMessageResultStatus.Succeeded
                                    ? 'Delivered'
                                    : result.status ===
                                      TestMessageResultStatus.Failed
                                    ? formatFailureDetail(result)
                                    : 'Pending'}
                            </span>
                        </DetailsRow>
                    ))}
                </DetailsList>
            )}
        </Container>
    );
};

const Container = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
}));

const Row = styled.div(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
}));

const Text = styled.p(() => ({
    fontSize: '14px',
    textAlign: 'center',
}));

const DetailsToggle = styled.a(() => ({
    fontSize: '13px',
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    fontWeight: 600,
}));

const DetailsList = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(0.5),
    width: '100%',
    maxWidth: '400px',
}));

const DetailsRow = styled.div(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
    padding: aampeTheme.spacing(0.5, 0),
}));

export default TestMessageDeliveryStatus;
